/**
 * Analytics Service for tracking user interactions
 * Provides a modular, extensible interface for event tracking
 */

import { environment, SARFINDER_BE_API_URL } from "utils/constants";

/**
 * A shared project's link carries its token in the path (/share/<token>).
 * The token names the link, so it stays out of the analytics, as the
 * backend keeps /api/projects/ out of its request log.
 */
export const redactShareToken = (value) =>
  typeof value === "string"
    ? value.replace(/\/share\/[A-Za-z0-9_-]+/g, "/share/[link]")
    : value;

class AnalyticsService {
  constructor(options = {}) {
    this.endpoint = options.endpoint || "/analytics/track/";
    this.batchEndpoint = options.batchEndpoint || "/analytics/track/batch/";
    this.enabled = options.enabled !== false;
    this.debug = options.debug === true;
    this.batchSize = options.batchSize || 10;
    this.flushInterval = options.flushInterval || 30000; // 30 seconds
    this.eventQueue = [];
    this.processors = options.processors || [];
    this.sessionKey = options.sessionKey || "analytics_session_id";

    // Start queue processing interval
    if (this.enabled) {
      this.flushIntervalId = setInterval(() => {
        this.flush();
      }, this.flushInterval);

      // Page views come from the router (usePageViewTracking in
      // utils/router.jsx), one per route change.

      // Send what's queued when the page is hidden or closed. visibilitychange
      // is the last event mobile browsers reliably fire.
      const flushNow = () => this.flush(true);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushNow();
      });
      window.addEventListener("pagehide", flushNow);
    }
  }

  /**
   * Track any type of event
   * @param {string} eventType - Type of event
   * @param {string} category - Event category
   * @param {Object} properties - Additional event properties
   * @returns {Promise|null} - Promise if immediate sending, null if queued
   */
  trackEvent(eventType, category, properties = {}, immediate = false) {
    if (!this.enabled) return null;

    const event = {
      event_type: eventType,
      category: category,
      properties: {
        ...properties,
        url: redactShareToken(window.location.href),
        path: redactShareToken(window.location.pathname),
        referrer: redactShareToken(document.referrer),
        timestamp: new Date().toISOString(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    };

    // Process the event through all processors
    const processedEvent = this.processors.reduce(
      (currentEvent, processor) => processor(currentEvent),
      event,
    );

    if (this.debug) {
      console.log("Analytics event:", processedEvent);
    }

    if (immediate) {
      return this.sendEvent(processedEvent);
    } else {
      this.eventQueue.push(processedEvent);

      // Auto-flush if we reach the batch size
      if (this.eventQueue.length >= this.batchSize) {
        this.flush();
      }

      return null;
    }
  }

  /**
   * Track a page view. Sent at once rather than queued: the response sets the
   * device cookie that ties this browser's API calls to the same visit.
   * @param {Object} additionalProps - Additional properties to track
   */
  trackPageView(additionalProps = {}) {
    return this.trackEvent("page_view", "view", additionalProps, true);
  }

  /**
   * Track a search event
   * @param {Object} searchParams - Search parameters
   * @param {number} resultCount - Number of results
   * @param {number} durationMs - Search duration in milliseconds
   */
  trackSearch(searchParams, resultCount = 0, durationMs = 0) {
    return this.trackEvent("search", "search", {
      query_params: searchParams,
      result_count: resultCount,
      duration_ms: durationMs,
    });
  }

  /**
   * Track an InSAR request
   * @param {string} requestType - Type of request (DPM1, DPM2, etc.)
   * @param {Object} details - Request details
   */
  trackRequest(requestType, details = {}) {
    return this.trackEvent("insar_request", "request", {
      request_type: requestType,
      ...details,
    });
  }

  /**
   * Track a user action
   * @param {string} action - Action name
   * @param {Object} details - Action details
   */
  trackAction(action, details = {}) {
    return this.trackEvent("user_action", "user", {
      action: action,
      ...details,
    });
  }

  /**
   * Track an error
   * @param {string} errorType - Type of error
   * @param {string} message - Error message
   * @param {string} stack - Error stack trace
   */
  trackError(errorType, message, stack = null) {
    return this.trackEvent(
      "error",
      "error",
      {
        error_type: errorType,
        message: message,
        stack: stack,
      },
      true,
    ); // Send errors immediately
  }

  /**
   * Send a single event to the server
   * @param {Object} event - Event to send
   * @returns {Promise} - Response promise
   */
  sendEvent(event) {
    return fetch(this.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
      credentials: "include", // Include cookies
    }).catch((error) => {
      // Log but don't throw to avoid breaking app functionality
      console.error("Error sending analytics event:", error);
    });
  }

  /**
   * Send multiple events in a batch
   * @param {Array} events - Events to send
   * @returns {Promise} - Response promise
   */
  sendBatchEvents(events) {
    if (!events || events.length === 0) return Promise.resolve();

    return fetch(this.batchEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ events }),
      credentials: "include", // Include cookies
    }).catch((error) => {
      // Log but don't throw to avoid breaking app functionality
      console.error("Error sending batch analytics events:", error);
    });
  }

  /**
   * Flush the event queue
   * @param {boolean} sync - Whether to use synchronous request
   */
  flush(sync = false) {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    if (sync && navigator.sendBeacon) {
      // Use navigator.sendBeacon for page unload. text/plain keeps it a
      // simple cross-origin request (no preflight, which a beacon can't do);
      // the backend parses the body as JSON regardless.
      const blob = new Blob([JSON.stringify({ events })], {
        type: "text/plain",
      });
      navigator.sendBeacon(this.batchEndpoint, blob);
    } else {
      this.sendBatchEvents(events);
    }
  }

  /**
   * Add a new event processor
   * @param {Function} processor - Processor function
   */
  addProcessor(processor) {
    if (typeof processor === "function") {
      this.processors.push(processor);
    }
  }

  /**
   * Disable analytics
   */
  disable() {
    this.enabled = false;
    clearInterval(this.flushIntervalId);
  }

  /**
   * Enable analytics
   */
  enable() {
    this.enabled = true;
    this.flushIntervalId = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }
}

// ---- Event Processors ----

// localStorage key for the device id. It keeps the name of the session
// processor that used it before, so browsers that already have one keep it.
const DEVICE_KEY = "analytics_session_id";

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id =
        window.crypto?.randomUUID?.() ||
        "d_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    // Storage blocked: the backend falls back to its device cookie.
    return undefined;
  }
}

/**
 * Identifies the browser and where it is. The backend groups a device's
 * events into visits (and mirrors the id into a cookie so API calls join
 * them), and maps the time zone to a country without an IP lookup.
 */
const deviceProcessor = (event) => ({
  ...event,
  device_id: getDeviceId(),
  properties: {
    ...event.properties,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  },
});

// Create default instance with standard configuration
const analytics = new AnalyticsService({
  enabled: true,
  // process.env.NODE_ENV === "production" ||
  // process.env.REACT_APP_ENABLE_ANALYTICS === "true",
  debug: environment === "DEVELOPMENT", //process.env.NODE_ENV !== "production",
  endpoint: `${SARFINDER_BE_API_URL}/analytics/track/`,
  batchEndpoint: `${SARFINDER_BE_API_URL}/analytics/track/batch/`,
  processors: [deviceProcessor],
});

export default analytics;
